import React, { useCallback, useContext, useMemo } from 'react'
import cn from 'clsx'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { includedGuestsExceededItems } from 'lib/cartReservationUtils'
import { rem } from 'polished'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPageSelectors'
import styled from 'styled-components'
import moment from 'moment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import TextButton from 'components/Luxkit/Button/TextButton'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Pill from 'components/Luxkit/Pill'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferPageBNBLcta from '../OfferPageBNBLcta'
import OfferBookButton from '../OfferBookButton'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/depositsUtils'
import { getWeeklyInstalmentAmount, isInstalmentsAvailableOnOffer } from 'lib/payInInstalmentsUtils'
import SecureWithFlexiblePaymentsPriceDetails from 'components/Common/SecureWithFlexiblePayments/SecureWithFlexiblePaymentsPriceDetails'
import { isFlexibleCancellationPolicy } from 'lib/cancellationPolicy'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { getInstalmentsAbTestEnabled } from 'selectors/featuresSelectors'
import { reserveForZeroEnabled } from 'checkout/selectors/featureConfig/reserveForZeroSelector'
import { isPackageEligibleForZeroReservation } from 'lib/reserveForZero'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import Clickable from 'components/Common/Clickable/Clickable'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import useToggle from 'hooks/useToggle'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { max } from 'lib/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import { pluralizeToString } from 'lib/pluralize'

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(20)};
`

const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;

  & + & {
    margin-left: ${rem(12)};
  }
`

const PriceButton = styled(OfferBookButton)`
  margin-top: ${rem(12)};
`

const PackagePrices = styled(Caption)`
  display: flex;
  position: relative;

  &.show-discount {
    margin-top: ${rem(8)};
  }

  > * {
    flex: 1 1 calc(50% - ${rem(12)});
  }

  > * + * {
    padding-left: ${rem(12)};
    border-left: 1px solid ${props => props.theme.palette.neutral.five};
    flex: 1 1 50%;
  }
`

const FlightsBundledNoCacheMessage = styled(BodyText)`
  margin-top: ${rem(12)};

  > * + * {
    margin-top: ${rem(12)};
  }
`

const ZeroBookingTextWrapper = styled(Clickable)`
  margin-top: ${rem(12)};
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const DurationCaption = styled(Caption)`
  margin-top: ${rem(8)};
`

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms: Array<App.Occupants>;
  checkIn: moment.Moment;
  fetching?: boolean;
  currency: string;
  instalmentsABTestEnabled: boolean;
  reserveForZeroEnabled: boolean;
  defaultDepositAmountPercentage: number;
}

function FlashHotelOptionPriceDetails(props: Props) {
  const {
    offer,
    packageView,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    fetching,
    currency,
    instalmentsABTestEnabled,
    reserveForZeroEnabled,
    defaultDepositAmountPercentage,
  } = props

  const {
    package: pkg,
    flightPrice,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
  } = packageView

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { showTaxExclusivePrices } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const { currentContact, currentRegionCode } = useContext(GeoContext)
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (packageView.flightsAvailable || offer.bundledWithFlightsOnly)
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoom, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = offer.bundledWithFlightsOnly ? bundledDiscountPercent : max([hotelDiscountPercent, bundledDiscountPercent])

  const tel = currentRegionCode === 'AU' ? '1300860454' : currentContact.local.number
  const humanReadable = currentRegionCode === 'AU' ? '1300 860 454' : currentContact.local.humanReadable
  const departureTooFarInFuture = useMemo(() => {
    return !!checkIn && checkIn.diff(moment.utc(), 'days') > 330
  }, [checkIn])

  const {
    hotelPrice,
    taxesAndFees,
    propertyFees,
  } = packageView

  const showPriceDetails = (taxesAndFees ?? 0) > 0 || (propertyFees ?? 0) > 0

  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + (propertyFees ?? 0)

  const showBNBL = packageView.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && config.accommodation.hasBnbl
  const hasFlightWithCachePrice = !!flightPrice && !offer.flightCacheDisabled
  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (offer.bundledWithFlightsOnly ? hasFlightWithCachePrice : true)
  const shouldShowValue = hotelValue > hotelPrice && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkIn, hotelPrice)
  const isInstalmentsEnabled = instalmentsABTestEnabled && isInstalmentsAvailableOnOffer(offer, currency, checkIn)
  const instalmentWeeklyAmount = getWeeklyInstalmentAmount(hotelPrice, checkIn, offer, defaultDepositAmountPercentage)
  const isIndiaRegion = currentRegionCode === 'IN'
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const [isReserveForZeroPolicyModalOpen, , openReserveForZeroModal, onClose] = useToggle()

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkIn)
  const showReserveForZero = reserveForZeroEnabled && roomRateIsReservableForZero
  const depositsOrInstalmentsEnabled = isDepositEnabled || isInstalmentsEnabled
  const showDepositTag = depositsOrInstalmentsEnabled && !reserveForZeroEnabled
  const showFlexiblePaymentTag = depositsOrInstalmentsEnabled && reserveForZeroEnabled

  const showModal = useContext(ModalContext)
  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      trackEvent('CHECK_AVAILABILITY_PRESSED')
    } else {
      trackEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, trackEvent])

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])

  return (<>
    <div>
      <Group direction="horizontal" gap={4} wrap="wrap">
        {shouldShowDiscount &&
          <ValueWrapper variant="medium" weight="bold">
            <Pill kind="primary" variant="success">Save up to {discountPercent}%</Pill>
          </ValueWrapper>
        }
        {showDepositTag && <SecureWithDepositTagAndModal
          isFlexibleCancellation={isFlexibleCancellation}
          offerType={offer.type}
          depositAmountPercentage={depositAmountPercentage}
          depositThresholds={offer.depositThresholds}
          isInstalmentsAvailable={isInstalmentsEnabled}
          cancellationPolicy={pkg.roomRate.cancellationPolicy}
          checkInDate={checkIn}
          propertyTimezoneOffset={offer.property.timezoneOffset}
        />}
        {showFlexiblePaymentTag && (
          <Clickable onClick={onFlexPaymentPolicyOpen}>
            <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
              Flexible payment options
            </Pill>
          </Clickable>
        )}
      </Group>
      <PackagePrices variant="medium" className={cn({ 'show-discount': shouldShowDiscount || isDepositEnabled })}>
        {!offer.bundledWithFlightsOnly &&
          <PackagePrice data-testid="pricing">
            <Group direction="vertical" gap={4}>
              <div>
                {flightsEnabled && <Caption variant="large" weight="bold">Hotel</Caption>}
                <DurationCaption variant="medium">Total for {pluralizeToString('night', pkg.duration)} {!checkIn && 'from'}</DurationCaption>
                <Heading variant="heading6" colour={exclusivePriceColor}>
                  <FormatCurrency value={totalPrice} format="roundedDollar" />
                </Heading>
                {(isInstalmentsEnabled && !!instalmentWeeklyAmount) &&
                  <SecureWithFlexiblePaymentsPriceDetails
                  currency={currency}
                  depositPercentage={depositAmountPercentage}
                  instalmentAmount={instalmentWeeklyAmount}
                  cancellationPolicy={pkg.roomRate.cancellationPolicy}
                  checkInDate={checkIn}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                />
              }
                <TaxesAndFees
                taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
                taxAmount={taxesAndFees}
              />
                {showIncludedGuestsExceeded && (
                  <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
                )}
                {shouldShowValue &&
                  <ValueWrapper variant="medium" weight="bold">
                    <span data-testid="value-box">
                      Valued up to <FormatCurrency value={hotelValue} />
                    </span>
                  </ValueWrapper>
              }
              </div>
              {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                type="exact"
                offer={offer}
                totalCost={totalPrice}
                numberOfNights={pkg.duration}
              />}
            </Group>
            {showPriceDetails &&
              <PriceDetailsModal
                  offerPage
                  offer={offer}
                  duration={pkg.duration}
                  taxesAndFees={taxesAndFees}
                  propertyFees={propertyFees}
                  hotelPrice={hotelPrice}
                  rooms={rooms}
                  extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                  dueAtPropertyMessage={offer.property.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                  cta={
                    <OfferBookButton
                      type="submit"
                      disabled={fetching}
                      pkg={packageView.package}
                      onSubmit={onSubmit}
                    >
                      {canBuyNow ? 'Book now' : 'Check availability'}
                    </OfferBookButton>
                  }
                />
              }
            {showReserveForZero &&
              <ZeroBookingTextWrapper onClick={openReserveForZeroModal}>
                <BodyText variant="small" colour="highlight" as="span" weight="bold">
                  Reserve now for free
                </BodyText>
                <SolidInfoCircleIcon size="XS" colour="primary"/>
              </ZeroBookingTextWrapper>
              }
            <PriceButton
              type="submit"
              data-testid="HotelBookingBtn"
              disabled={fetching}
              pkg={packageView.package}
              onSubmit={onSubmit}
            >
              {canBuyNow ? 'Book now' : 'Check availability'}
            </PriceButton>
          </PackagePrice>
        }

        {flightsEnabled &&
          <PackagePrice data-testid="flights-pricing">
            <div>
              {!flightsBundledNoCache && <Caption variant="large" weight="bold">
                Hotel + flights
              </Caption>}
              {!hasFlights && flightOrigin.code === flightDestination?.code && (
                <ChangeDepartureWrapper>
                  <Caption variant="large" colour="neutral-three">
                    No flights available from {flightOrigin.name }. Please select another airport.
                  </Caption>
                  <TextButton
                kind="tertiary"
                onClick={onToggleFilter}
                data-filter="airport"
                >
                    Change departure airport
                  </TextButton>
                </ChangeDepartureWrapper>
              )}
              {!hasFlights && !flightsBundledNoCache && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && (
                <Caption variant="large" colour="neutral-three">
                  {!isIndiaRegion && 'Add flights to the hotel package. Select from all major airlines.'}
                  {isIndiaRegion && 'Add flights to your hotel package from the best available airlines.'}
                </Caption>)}
              {!hasFlights && departureTooFarInFuture && (
                <ChangeDepartureWrapper>
                  <Caption variant="large" colour="neutral-three">
                    Flights can only be booked up to 11 months in advance.
                  </Caption>
                </ChangeDepartureWrapper>
              )}
              {hasFlights && <>
                {checkIn && <DurationCaption variant="medium">
                  Total for {pluralizeToString('night', pkg.duration)}
                </DurationCaption>}
                {!checkIn && <DurationCaption variant="medium">
                  {pluralizeToString('night', pkg.duration)} from
                </DurationCaption>}
                <Heading variant="heading6" colour={exclusivePriceColor}>
                  <FormatCurrency value={totalPrice + flightPrice} format="roundedDollar" /><Caption variant="medium" as="sup">*</Caption>
                </Heading>
                {!rooms && <Caption variant="medium" colour="neutral-three">
                  Two Adults
                </Caption>}
                <TaxesAndFees
                  taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
                  taxAmount={taxesAndFees}
                  />
                {showIncludedGuestsExceeded && (
                  <AdditionalGuestsPopup
                    complex={false}
                    description="+ Extra guest surcharge"
                    modalContent={pkg.roomPolicyDescription}
                  />
                )}
                {shouldShowValue &&
                  <ValueWrapper variant="medium" weight="bold">
                    <div data-testid="value-box">
                      Valued up to <FormatCurrency value={hotelValue + flightPrice} />
                    </div>
                  </ValueWrapper>
                }
                {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                  type="exact"
                  offer={offer}
                  totalCost={totalPrice}
                  numberOfNights={pkg.duration}
                />}
                {showPriceDetails &&
                  <PriceDetailsModal
                  offerPage
                  offer={offer}
                  duration={pkg.duration}
                  taxesAndFees={taxesAndFees}
                  propertyFees={propertyFees}
                  hotelPrice={hotelPrice}
                  flightPrice={flightPrice}
                  rooms={rooms}
                  extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                  dueAtPropertyMessage={offer.property.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                  cta={
                    <OfferBookButton
                      type="submit"
                      disabled={fetching}
                      pkg={packageView.package}
                      data-buytype="flights"
                      onSubmit={onSubmit}
                    >
                      {canBuyNow ? 'Book now' : 'Check availability'}
                    </OfferBookButton>
              }
            />}
              </>}
              <AtolProtectedTooltip />
            </div>
            {flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && (
              <PriceButton
                type="submit"
                data-testid="HotelAndFlightsBtn"
                disabled={fetching}
                pkg={packageView.package}
                data-buytype="flights"
                onSubmit={onSubmit}
              >
                {canBuyNow ? 'Book now' : 'Check availability'}
              </PriceButton>
            )}
            {flightsBundledNoCache &&
              <FlightsBundledNoCacheMessage variant="medium" align="center">
                <BodyText variant="small">OR</BodyText>
                <BodyText variant="medium" weight="bold">
                  Call our concierge team for<br />exclusive prices & priority booking</BodyText>
                <TextButton
                  kind="secondary"
                  fit="full-width"
                  startIcon={<LinePhoneIcon />}
                  href={`tel:${tel}`}
                  rel="nofollow"
                >
                  {humanReadable}
                </TextButton>
              </FlightsBundledNoCacheMessage>}
          </PackagePrice>
        }

        <LoadingIndicator visible={fetching} floating opaque />
      </PackagePrices>
    </div>
    {showBNBL && <OfferPageBNBLcta packageView={packageView} />}
    {showReserveForZero &&
      <ReserveForZeroPolicyModal
        isOpen={isReserveForZeroPolicyModalOpen}
        onClose={onClose}
      />
    }
  </>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  instalmentsABTestEnabled: getInstalmentsAbTestEnabled(appState),
  reserveForZeroEnabled: reserveForZeroEnabled(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(FlashHotelOptionPriceDetails)
